import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeftRight, Plus } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiDownload } from "../lib/api";
import { fmtDate, rm } from "../lib/format";
import { toast } from "../stores/toast";
import { useBottomBarActions } from "../hooks/useBottomBarActions";
import {
  Badge,
  type BadgeColor,
  Button,
  Input,
  SearchSelect,
  statusColor,
  Textarea,
  PillSegmentSwitcher,
  Toggle,
  useConfirm,
} from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { ImageHoverPreview } from "../components/ImageHoverPreview";
import { RightSidebarPanel } from "../components/RightSidebarPanel";

const CONDITION_OPTIONS = [
  "Near Mint",
  "Lightly Played",
  "Moderately Played",
  "Heavily Played",
  "Damaged",
].map((c) => ({ value: c, label: c }));

const PRODUCT_KIND_OPTIONS = [
  { value: "SINGLE", label: "Single card" },
  { value: "SEALED", label: "Sealed product" },
  { value: "BUNDLE", label: "Pack bundle" },
];

const SEALED_TYPE_OPTIONS = [
  { value: "ETB", label: "Elite Trainer Box" },
  { value: "BOOSTER_BOX", label: "Booster box" },
  { value: "BOOSTER_PACK", label: "Booster pack" },
  { value: "BLISTER", label: "Blister" },
  { value: "COLLECTION_BOX", label: "Collection box" },
  { value: "BUNDLE", label: "Multi-pack bundle" },
  { value: "OTHER", label: "Other sealed" },
];

type ProductKind = "SINGLE" | "SEALED" | "BUNDLE";
type SealedType =
  | "ETB"
  | "BOOSTER_BOX"
  | "BOOSTER_PACK"
  | "BLISTER"
  | "COLLECTION_BOX"
  | "BUNDLE"
  | "OTHER";

interface CardFields {
  productKind?: ProductKind;
  sealedType?: SealedType | null;
  bundleQty?: number | string | null;
  game?: string;
  set?: string;
  name?: string;
  number?: string;
  language?: string;
  variant?: string;
  rarity?: string;
  condition?: string;
  graded?: boolean;
  gradeLabel?: string;
  confidence?: number;
  estPriceMYR?: number | null;
  notes?: string;
}

function confidenceColor(c: number | undefined): BadgeColor {
  if (c == null) return "gray";
  if (c >= 0.85) return "green";
  if (c >= 0.6) return "amber";
  return "red";
}

interface CardItem {
  id: string;
  sessionId: string;
  frontUrl: string;
  backUrl: string | null;
  status: string;
  fields: CardFields | null;
  price: number | null;
  quantity: number;
  productId: string | null;
  error: string | null;
  createdAt: string;
}

interface SessionSummary {
  id: string;
  name: string;
  createdAt: string;
  counts: Record<string, number>;
  total: number;
}

interface SessionDetail {
  session: { id: string; name: string; createdAt: string };
  items: CardItem[];
}

function needsPolling(items: CardItem[] | undefined): boolean {
  return (items ?? []).some((i) => i.status === "QUEUED" || i.status === "IDENTIFYING");
}

function canClearSession(session: SessionSummary): boolean {
  return (session.counts.PUBLISHED ?? 0) === 0;
}

function slugifyLabel(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function previewCategoryPath(fields: CardFields): string {
  const game = (fields.game ?? "").trim();
  const lang = (fields.language ?? "").trim().toUpperCase();
  let parent = "?";
  if (game.toLowerCase() === "pokemon" && (lang === "JP" || lang === "JPN")) parent = "pokemon-japan";
  else if (game.toLowerCase() === "pokemon") parent = "pokemon";
  else if (game.toLowerCase() === "one piece") parent = "one-piece";
  else if (game) parent = slugifyLabel(game);

  let leaf = "raw";
  if (fields.productKind === "SEALED" || fields.productKind === "BUNDLE") {
    switch (fields.sealedType) {
      case "ETB":
        leaf = "sealed-etb";
        break;
      case "BOOSTER_BOX":
        leaf = "sealed-booster-box";
        break;
      case "BOOSTER_PACK":
        leaf = "sealed-booster-pack";
        break;
      case "BLISTER":
        leaf = "sealed-blister";
        break;
      case "COLLECTION_BOX":
        leaf = "sealed-collection-box";
        break;
      case "BUNDLE":
        leaf = "sealed-bundle";
        break;
      default:
        leaf = "sealed";
    }
  } else if (fields.graded && fields.gradeLabel?.trim()) {
    leaf = slugifyLabel(fields.gradeLabel);
  }

  return `${parent} → ${leaf}`;
}

function productKindLabel(kind: ProductKind | undefined): string {
  if (kind === "SEALED") return "Sealed";
  if (kind === "BUNDLE") return "Bundle";
  return "Single";
}

function isSealedFields(fields: CardFields): boolean {
  return fields.productKind === "SEALED" || fields.productKind === "BUNDLE";
}

type UploadMode = "ai" | "manual";

function isManualEntryItem(item: CardItem): boolean {
  const f = item.fields;
  return (f?.confidence ?? 0) === 0 && !(f?.name ?? "").trim();
}

function manualPairCount(files: File[], productTail: boolean): number {
  const odd = files.length % 2 === 1;
  const pairFileCount = odd && productTail ? files.length - 1 : files.length;
  return Math.floor(pairFileCount / 2);
}

export function CardUploader() {
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const fileRef = useRef<HTMLInputElement>(null);
  const manualAddRef = useRef<HTMLInputElement>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editItem, setEditItem] = useState<CardItem | null>(null);
  const [fieldsDraft, setFieldsDraft] = useState<CardFields>({});
  const [priceDraft, setPriceDraft] = useState<string>("");
  const [qtyDraft, setQtyDraft] = useState("1");
  const [dragOver, setDragOver] = useState(false);
  const [uploadMode, setUploadMode] = useState<UploadMode>("manual");
  const [manualPendingFiles, setManualPendingFiles] = useState<File[] | null>(null);
  const [manualProductTail, setManualProductTail] = useState(true);
  const [manualReviewDragOver, setManualReviewDragOver] = useState(false);
  const [publishErrors, setPublishErrors] = useState<{ itemId: string; code: string; message: string }[]>([]);

  const { data: sessions, isLoading: sessionsLoading } = useQuery({
    queryKey: ["carduploader", "sessions"],
    queryFn: () => api<{ items: SessionSummary[] }>("/carduploader/sessions").then((r) => r.items),
    enabled: !sessionId,
  });

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ["carduploader", "session", sessionId],
    queryFn: () => api<SessionDetail>(`/carduploader/sessions/${sessionId}`),
    enabled: !!sessionId,
    refetchInterval: (q) => (needsPolling(q.state.data?.items) ? 3000 : false),
  });

  const createMut = useMutation({
    mutationFn: (name?: string) => api<{ id: string }>("/carduploader/sessions", { body: { name } }),
    onSuccess: (res) => {
      toast("success", "Session created");
      setSessionId(res.id);
      void queryClient.invalidateQueries({ queryKey: ["carduploader", "sessions"] });
    },
    onError: (e: Error) => toast("error", e.message),
  });

  useBottomBarActions(() => {
    if (sessionId) return [];
    return [
      {
        id: "create-session",
        label: "Create",
        icon: Plus,
        variant: "primary" as const,
        disabled: createMut.isPending,
        onClick: () => createMut.mutate(undefined),
      },
    ];
  });

  const uploadMut = useMutation({
    mutationFn: async (files: FileList | File[]) => {
      const formData = new FormData();
      for (const file of files) formData.append("files", file);
      return api<{ items: CardItem[]; skipped?: { fileName: string; reason: string }[] }>(
        `/carduploader/sessions/${sessionId}/upload`,
        { formData },
      );
    },
    onSuccess: (data) => {
      const queued = data.items.filter((i) => i.status !== "DUPLICATE_SKIPPED").length;
      const skipped = data.skipped?.length ?? data.items.filter((i) => i.status === "DUPLICATE_SKIPPED").length;
      if (queued > 0) {
        toast("success", `${queued} photo${queued === 1 ? "" : "s"} queued for identification`);
      }
      if (skipped > 0) {
        toast("error", `${skipped} skipped — already in catalog or this session`);
      }
      if (queued === 0 && skipped === 0) {
        toast("success", "Upload complete");
      }
      void queryClient.invalidateQueries({ queryKey: ["carduploader", "session", sessionId] });
    },
    onError: (e: Error) => toast("error", e.message),
  });

  const uploadPairsMut = useMutation({
    mutationFn: async (args: { files: File[]; productTail: boolean }) => {
      const formData = new FormData();
      for (const file of args.files) formData.append("files", file);
      formData.append("productTail", args.productTail ? "true" : "false");
      return api<{ items: CardItem[]; skipped?: { fileName: string; reason: string }[] }>(
        `/carduploader/sessions/${sessionId}/upload-pairs`,
        { formData },
      );
    },
    onSuccess: (data) => {
      const created = data.items.filter((i) => i.status !== "DUPLICATE_SKIPPED").length;
      const skipped = data.skipped?.length ?? data.items.filter((i) => i.status === "DUPLICATE_SKIPPED").length;
      if (created > 0) {
        toast("success", `${created} item${created === 1 ? "" : "s"} ready for manual entry`);
      }
      if (skipped > 0) {
        toast("error", `${skipped} skipped — already in catalog or this session`);
      }
      setManualPendingFiles(null);
      void queryClient.invalidateQueries({ queryKey: ["carduploader", "session", sessionId] });
    },
    onError: (e: Error) => toast("error", e.message),
  });

  const saveItemMut = useMutation({
    mutationFn: (opts: { approve?: boolean; reject?: boolean }) => {
      const fields = { ...fieldsDraft };
      if (typeof fields.bundleQty === "string") {
        fields.bundleQty = fields.bundleQty === "" ? null : Number.parseInt(fields.bundleQty, 10) || 1;
      }
      return api(`/carduploader/items/${editItem!.id}`, {
        method: "PUT",
        body: {
          fields,
          price: priceDraft === "" ? null : Number(priceDraft),
          quantity: Number.parseInt(qtyDraft, 10) || 1,
          approve: opts.approve,
          reject: opts.reject,
        },
      });
    },
    onSuccess: (_d, opts) => {
      toast("success", opts.reject ? "Card rejected" : opts.approve ? "Card approved" : "Card saved");
      setEditItem(null);
      void queryClient.invalidateQueries({ queryKey: ["carduploader", "session", sessionId] });
    },
    onError: (e: Error) => toast("error", e.message),
  });

  const publishMut = useMutation({
    mutationFn: (itemIds?: string[]) =>
      api<{
        results: unknown[];
        errors: { itemId: string; code: string; message: string }[];
        categoriesCreated?: string[];
      }>(`/carduploader/sessions/${sessionId}/publish`, {
        body: itemIds?.length ? { itemIds } : {},
      }),
    onSuccess: (data) => {
      if (data.results.length > 0) {
        toast("success", `Published ${data.results.length} card${data.results.length === 1 ? "" : "s"} to warehouse`);
      }
      if (data.categoriesCreated?.length) {
        toast("success", `Created categories: ${data.categoriesCreated.join(", ")}`);
        void queryClient.invalidateQueries({ queryKey: ["catalog-categories"] });
      }
      if (data.errors.length > 0) {
        toast("error", `${data.errors.length} failed — check card fields`);
        setPublishErrors(data.errors);
      } else {
        setPublishErrors([]);
      }
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ["carduploader", "session", sessionId] });
    },
    onError: (e: Error) => toast("error", e.message),
  });

  const retryMut = useMutation({
    mutationFn: (id: string) => api(`/carduploader/items/${id}/retry`, { method: "POST" }),
    onSuccess: () => {
      toast("success", "Retry queued");
      void queryClient.invalidateQueries({ queryKey: ["carduploader", "session", sessionId] });
    },
    onError: (e: Error) => toast("error", e.message),
  });

  const bulkMut = useMutation({
    mutationFn: (action: "approve" | "retry" | "reject") =>
      api<{ counts: { ok: number; skipped: number } }>(`/carduploader/sessions/${sessionId}/items/bulk`, {
        method: "POST",
        body: { action, itemIds: [...selected] },
      }),
    onSuccess: (data, action) => {
      const verb =
        action === "approve" ? "approved" : action === "retry" ? "queued for retry" : "rejected";
      if (data.counts.ok > 0) {
        toast("success", `${data.counts.ok} card${data.counts.ok === 1 ? "" : "s"} ${verb}`);
      }
      if (data.counts.skipped > 0) {
        toast("error", `${data.counts.skipped} skipped — wrong status for this action`);
      }
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ["carduploader", "session", sessionId] });
    },
    onError: (e: Error) => toast("error", e.message),
  });

  const clearSessionMut = useMutation({
    mutationFn: (id: string) => api(`/carduploader/sessions/${id}`, { method: "DELETE" }),
    onSuccess: (_data, id) => {
      toast("success", "Session cleared");
      if (sessionId === id) {
        setSessionId(null);
        setSelected(new Set());
      }
      setSelectedSessions((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      void queryClient.invalidateQueries({ queryKey: ["carduploader", "sessions"] });
    },
    onError: (e: Error) => toast("error", e.message),
  });

  const deleteItemMut = useMutation({
    mutationFn: (id: string) => api(`/carduploader/items/${id}`, { method: "DELETE" }),
    onSuccess: (_data, id) => {
      toast("success", "Card removed");
      if (editItem?.id === id) setEditItem(null);
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      void queryClient.invalidateQueries({ queryKey: ["carduploader", "session", sessionId] });
    },
    onError: (e: Error) => toast("error", e.message),
  });

  const openEdit = useCallback((item: CardItem) => {
    setEditItem(item);
    setFieldsDraft({
      productKind: "SINGLE",
      ...(item.fields ?? {}),
    });
    setPriceDraft(item.price != null ? String(item.price) : "");
    setQtyDraft(String(item.quantity));
  }, []);

  const appendManualFiles = useCallback((incoming: FileList | File[]) => {
    const added = Array.from(incoming);
    if (!added.length) return;
    let nextLen = 0;
    setManualPendingFiles((prev) => {
      const next = prev ? [...prev, ...added] : added;
      nextLen = next.length;
      return next;
    });
    if (nextLen % 2 === 1) setManualProductTail(true);
  }, []);

  const swapManualPair = useCallback((pairIndex: number) => {
    setManualPendingFiles((prev) => {
      if (!prev) return prev;
      const frontIdx = pairIndex * 2;
      const backIdx = frontIdx + 1;
      if (backIdx >= prev.length) return prev;
      const next = [...prev];
      const front = next[frontIdx];
      next[frontIdx] = next[backIdx]!;
      next[backIdx] = front!;
      return next;
    });
  }, []);

  const onDrop = useCallback(
    (files: FileList | null) => {
      if (!files?.length || !sessionId) return;
      if (uploadMode === "manual") {
        if (manualPendingFiles) {
          appendManualFiles(files);
          return;
        }
        setManualPendingFiles(Array.from(files));
        if (files.length % 2 === 1) setManualProductTail(true);
        return;
      }
      uploadMut.mutate(files);
    },
    [sessionId, uploadMode, uploadMut, manualPendingFiles, appendManualFiles],
  );

  const manualPreviewUrls = useMemo(() => {
    if (!manualPendingFiles?.length) return [];
    return manualPendingFiles.map((f) => URL.createObjectURL(f));
  }, [manualPendingFiles]);

  useEffect(() => {
    return () => {
      for (const url of manualPreviewUrls) URL.revokeObjectURL(url);
    };
  }, [manualPreviewUrls]);

  const manualOddCount = (manualPendingFiles?.length ?? 0) % 2 === 1;
  const manualCanContinue =
    !!manualPendingFiles &&
    manualPendingFiles.length >= 2 &&
    (!manualOddCount || manualProductTail) &&
    !uploadPairsMut.isPending;

  const manualReviewOpen = Boolean(manualPendingFiles && uploadMode === "manual");

  const approvedIds = useMemo(
    () => (detail?.items ?? []).filter((i) => i.status === "APPROVED").map((i) => i.id),
    [detail?.items],
  );

  const selectedItems = useMemo(
    () => (detail?.items ?? []).filter((i) => selected.has(i.id)),
    [detail?.items, selected],
  );

  const approvableCount = useMemo(
    () => selectedItems.filter((i) => i.status === "IDENTIFIED" || i.status === "REJECTED").length,
    [selectedItems],
  );
  const retryableCount = useMemo(
    () => selectedItems.filter((i) => i.status === "FAILED").length,
    [selectedItems],
  );
  const rejectableCount = useMemo(
    () => selectedItems.filter((i) => i.status === "IDENTIFIED" || i.status === "APPROVED").length,
    [selectedItems],
  );
  const publishableCount = useMemo(
    () => selectedItems.filter((i) => i.status === "APPROVED").length,
    [selectedItems],
  );

  const sessionRowActions = useMemo(
    () => ({
      onDelete: async (row: SessionSummary) => {
        if (!canClearSession(row)) {
          toast("error", "Cannot clear a session with published cards");
          return;
        }
        await clearSessionMut.mutateAsync(row.id);
      },
    }),
    [clearSessionMut],
  );

  const sessionSelection = useMemo(
    () => ({
      entityLabel: "Session",
      onDelete: async (rows: SessionSummary[]) => {
        const blocked = rows.filter((row) => !canClearSession(row));
        if (blocked.length > 0) {
          toast("error", "Some selected sessions have published cards and cannot be cleared");
          return;
        }
        await Promise.all(rows.map((row) => clearSessionMut.mutateAsync(row.id)));
      },
    }),
    [clearSessionMut],
  );

  const itemRowActions = useMemo(
    () => ({
      onDelete: async (row: CardItem) => {
        if (row.status === "PUBLISHED") {
          toast("error", "Published cards cannot be deleted");
          return;
        }
        await deleteItemMut.mutateAsync(row.id);
      },
    }),
    [deleteItemMut],
  );

  const itemSelection = useMemo(
    () => ({
      entityLabel: "Card",
      onDelete: async (rows: CardItem[]) => {
        const blocked = rows.some((row) => row.status === "PUBLISHED");
        if (blocked) {
          toast("error", "Published cards cannot be deleted");
          return;
        }
        await Promise.all(rows.map((row) => deleteItemMut.mutateAsync(row.id)));
      },
      trailing: (
        <>
          <Button
            size="sm"
            variant="outline"
            disabled={bulkMut.isPending || retryableCount === 0}
            onClick={() => bulkMut.mutate("retry")}
          >
            Retry ({retryableCount})
          </Button>
          <Button
            size="sm"
            variant="danger"
            disabled={bulkMut.isPending || rejectableCount === 0}
            onClick={() => bulkMut.mutate("reject")}
          >
            Reject ({rejectableCount})
          </Button>
          <Button
            size="sm"
            disabled={bulkMut.isPending || approvableCount === 0}
            onClick={() => bulkMut.mutate("approve")}
          >
            Approve ({approvableCount})
          </Button>
          <Button
            size="sm"
            disabled={publishMut.isPending || publishableCount === 0}
            onClick={() => publishMut.mutate(selectedItems.filter((i) => i.status === "APPROVED").map((i) => i.id))}
          >
            Publish ({publishableCount})
          </Button>
        </>
      ),
    }),
    [
      deleteItemMut,
      bulkMut,
      publishMut,
      approvableCount,
      retryableCount,
      rejectableCount,
      publishableCount,
      selectedItems,
    ],
  );

  const sessionColumns: Column<SessionSummary>[] = [
    {
      key: "name",
      header: "Session",
      sortable: true,
      sortValue: (r) => r.name,
      render: (r) => <span className="font-medium">{r.name}</span>,
    },
    {
      key: "total",
      header: "Cards",
      className: "text-right",
      sortable: true,
      sortValue: (r) => r.total,
      render: (r) => r.total,
    },
    {
      key: "status",
      header: "Status",
      hideSm: true,
      sortable: true,
      sortValue: (r) => Object.keys(r.counts).join(","),
      exportValue: (r) => Object.entries(r.counts).map(([st, n]) => `${st} ${n}`).join("; "),
      render: (r) => (
        <div className="flex flex-wrap gap-1">
          {Object.entries(r.counts).map(([st, n]) => (
            <Badge key={st} color={statusColor(st)}>
              {st} {n}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: "created",
      header: "Created",
      className: "text-right",
      sortable: true,
      sortValue: (r) => r.createdAt,
      exportValue: (r) => fmtDate(r.createdAt),
      render: (r) => <span className="text-white/50">{fmtDate(r.createdAt)}</span>,
    },
  ];

  const itemColumns: Column<CardItem>[] = [
    {
      key: "thumb",
      header: "",
      className: "w-16",
      render: (r) => (
        <div className="flex gap-1">
          <ImageHoverPreview src={r.frontUrl}>
            <img src={r.frontUrl} alt="" className="aspect-[5/7] h-20 object-contain" />
          </ImageHoverPreview>
          {r.backUrl ? (
            <ImageHoverPreview src={r.backUrl}>
              <img src={r.backUrl} alt="" className="aspect-[5/7] h-20 object-contain opacity-90" />
            </ImageHoverPreview>
          ) : null}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      sortValue: (r) => r.status,
      render: (r) => (
        <div className="min-w-[100px]">
          <Badge color={statusColor(r.status)}>{r.status.replace(/_/g, " ")}</Badge>
          {(r.status === "FAILED" || r.status === "DUPLICATE_SKIPPED") && r.error && (
            <p className="mt-0.5 max-w-[200px] text-[10px] leading-snug text-red-300/90">{r.error}</p>
          )}
        </div>
      ),
    },
    {
      key: "kind",
      header: "Kind",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.fields?.productKind ?? "SINGLE",
      exportValue: (r) => productKindLabel(r.fields?.productKind),
      render: (r) =>
        r.status === "DUPLICATE_SKIPPED" ? (
          <span className="text-white/35">—</span>
        ) : (
          <Badge color={isSealedFields(r.fields ?? {}) ? "blue" : "gray"}>
            {productKindLabel(r.fields?.productKind)}
          </Badge>
        ),
    },
    {
      key: "card",
      header: "Product",
      sortable: true,
      sortValue: (r) => r.fields?.name ?? "",
      exportValue: (r) => [r.fields?.name, r.fields?.set, r.fields?.number].filter(Boolean).join(" · ") || "Awaiting ID",
      render: (r) => {
        if (r.status === "DUPLICATE_SKIPPED") {
          return (
            <div className="min-w-[140px]">
              <p className="font-medium text-white/60">{r.error ?? "Duplicate image"}</p>
              {r.productId && <p className="text-[10px] text-white/35">Linked product · {r.productId.slice(-8)}</p>}
            </div>
          );
        }
        const low = (r.fields?.confidence ?? 1) < 0.6 && r.status === "IDENTIFIED" && !isManualEntryItem(r);
        const manualEntry = r.status === "IDENTIFIED" && isManualEntryItem(r);
        const subtitle = isSealedFields(r.fields ?? {})
          ? [r.fields?.set, r.fields?.sealedType?.replace(/_/g, " ")].filter(Boolean).join(" · ")
          : [r.fields?.set, r.fields?.number].filter(Boolean).join(" · ");
        const pendingLabel = manualEntry ? "Enter details" : "Awaiting ID";
        return (
          <div className="min-w-[140px]">
            <p className={`font-medium ${low ? "text-amber-200" : ""}`}>{r.fields?.name?.trim() ? r.fields.name : "—"}</p>
            <p className="text-[10px] text-white/45">
              {subtitle || pendingLabel}
              {low ? " · needs review" : ""}
            </p>
          </div>
        );
      },
    },
    {
      key: "confidence",
      header: "Conf.",
      hideSm: true,
      className: "text-right",
      sortable: true,
      sortValue: (r) => r.fields?.confidence ?? -1,
      exportValue: (r) => (r.fields?.confidence != null ? Math.round(r.fields.confidence * 100) : ""),
      render: (r) =>
        r.fields?.confidence != null ? (
          <Badge color={confidenceColor(r.fields.confidence)}>
            {Math.round(r.fields.confidence * 100)}%
          </Badge>
        ) : (
          "—"
        ),
    },
    {
      key: "grade",
      header: "Grade",
      hideSm: true,
      sortable: true,
      sortValue: (r) => (r.fields?.graded ? r.fields.gradeLabel ?? "Graded" : "Raw"),
      exportValue: (r) => (r.fields?.graded ? r.fields.gradeLabel || "Graded" : "Raw"),
      render: (r) =>
        r.status === "DUPLICATE_SKIPPED" || isSealedFields(r.fields ?? {}) ? (
          "—"
        ) : r.fields?.graded ? (
          <Badge color="blue">{r.fields.gradeLabel || "Graded"}</Badge>
        ) : (
          <span className="text-white/35">Raw</span>
        ),
    },
    {
      key: "price",
      header: "Price",
      className: "text-right",
      sortable: true,
      sortValue: (r) => r.price ?? -1,
      exportValue: (r) => r.price ?? "",
      render: (r) => <span className="font-display font-semibold">{rm(r.price)}</span>,
    },
    {
      key: "qty",
      header: "Qty",
      hideSm: true,
      className: "text-right tabular-nums",
      sortable: true,
      sortValue: (r) => r.quantity,
      render: (r) => <span className="font-display font-semibold tabular-nums">{r.quantity}</span>,
    },
  ];

  if (!sessionId) {
    return (
      <>
        <ListPageShell>
          <DataTable
            columns={sessionColumns}
            rows={sessions}
            rowKey={(r) => r.id}
            loading={sessionsLoading}
            exportFilename="card-uploader-sessions"
          selectable
          selectedKeys={selectedSessions}
            onSelectedKeysChange={setSelectedSessions}
            rowActions={sessionRowActions}
            selection={sessionSelection}
            entityLabel="Session"
            onRowClick={(r) => setSessionId(r.id)}
            empty={{ title: "No upload sessions", hint: "Create a session to start batching card photos." }}
          />
        </ListPageShell>
        {dialog}
      </>
    );
  }

  return (
    <>
      <ListPageShell>
        <div className="flex shrink-0 flex-wrap items-start justify-between gap-3">
          <div>
            <button
              onClick={() => {
                setSessionId(null);
                setSelected(new Set());
              }}
              className="mb-1 text-xs text-brand-pink hover:underline"
            >
              ← All sessions
            </button>
            <h1 className="font-display text-lg font-bold">{detail?.session.name ?? "Session"}</h1>
            <p className="text-xs text-white/45">{detail?.items.length ?? 0} items</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="danger"
              disabled={clearSessionMut.isPending || (detail?.items.some((i) => i.status === "PUBLISHED") ?? false)}
              onClick={() =>
                confirm({
                  title: "Clear session?",
                  message: "This deletes the session and all cards in it. Published cards cannot be cleared.",
                  confirmLabel: "Clear",
                  danger: true,
                  action: () => clearSessionMut.mutate(sessionId!),
                })
              }
            >
              Clear session
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                void apiDownload(
                  `/carduploader/sessions/${sessionId}/export.csv`,
                  `cards-${sessionId.slice(-6)}.csv`,
                )
              }
            >
              Export CSV
            </Button>
            <Button
              size="sm"
              disabled={publishMut.isPending || approvedIds.length === 0}
              onClick={() => {
                const ids = approvedIds.filter((id) => selected.has(id));
                publishMut.mutate(ids.length ? ids : undefined);
              }}
            >
              Publish
              {selected.size
                ? ` (${approvedIds.filter((id) => selected.has(id)).length})`
                : approvedIds.length
                  ? " all approved"
                  : ""}
            </Button>
          </div>
        </div>

        <div className={manualReviewOpen ? "flex min-h-0 flex-1 flex-col gap-4" : "shrink-0 space-y-4"}>
          <div className={manualReviewOpen ? "shrink-0" : undefined}>
            <PillSegmentSwitcher
              options={[
                { value: "manual" as const, label: "Manual pairs" },
                { value: "ai" as const, label: "AI identify" },
              ]}
              value={uploadMode}
              onChange={(v) => {
                setUploadMode(v);
                setManualPendingFiles(null);
              }}
            />
          </div>

          {manualReviewOpen && manualPendingFiles ? (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-line bg-surface px-4 py-4">
              <div className="shrink-0">
                <p className="text-sm font-medium text-foreground">Review pairing</p>
                <p className="mt-1 text-xs text-muted">
                  Pairs run front → back in file order. Swap if a side is reversed.
                  {manualOddCount ? " The last image can be a sealed product photo." : ""}
                </p>
              </div>
              <div className="mt-4 min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain pr-1">
                {Array.from({ length: manualPairCount(manualPendingFiles, manualProductTail) }).map((_, idx) => {
                  const frontIdx = idx * 2;
                  const backIdx = frontIdx + 1;
                  return (
                    <div key={`${frontIdx}-${backIdx}-${manualPreviewUrls[frontIdx]}`} className="rounded-xl border border-line bg-surface-2/50 p-3">
                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted">Card {idx + 1}</p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => swapManualPair(idx)}
                          disabled={uploadPairsMut.isPending}
                        >
                          <ArrowLeftRight size={14} className="mr-1.5 shrink-0" />
                          Swap
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <div>
                          <p className="mb-1 text-[10px] text-muted">Front</p>
                          <img
                            src={manualPreviewUrls[frontIdx]}
                            alt=""
                            className="h-28 rounded-lg object-contain ring-1 ring-line"
                          />
                        </div>
                        <div>
                          <p className="mb-1 text-[10px] text-muted">Back</p>
                          <img
                            src={manualPreviewUrls[backIdx]}
                            alt=""
                            className="h-28 rounded-lg object-contain ring-1 ring-line"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
                {manualOddCount ? (
                  <div className="rounded-xl border border-line bg-surface-2/50 p-3">
                    <Toggle
                      label="Last image is sealed product"
                      checked={manualProductTail}
                      onChange={setManualProductTail}
                    />
                    {manualProductTail ? (
                      <div className="mt-3">
                        <p className="mb-1 text-[10px] text-muted">Product photo</p>
                        <img
                          src={manualPreviewUrls[manualPendingFiles.length - 1]}
                          alt=""
                          className="h-28 rounded-lg object-contain ring-1 ring-line"
                        />
                      </div>
                    ) : (
                      <p className="mt-2 text-xs text-muted">
                        Add one more image to complete the last pair, or turn the toggle on for a product photo.
                      </p>
                    )}
                  </div>
                ) : null}
              </div>
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setManualReviewDragOver(true);
                }}
                onDragLeave={() => setManualReviewDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setManualReviewDragOver(false);
                  if (e.dataTransfer.files?.length) appendManualFiles(e.dataTransfer.files);
                }}
                className={`mt-4 shrink-0 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors ${
                  manualReviewDragOver
                    ? "border-brand-pink bg-brand-pink/10"
                    : "border-line bg-surface-2/30 hover:border-line"
                }`}
              >
                <input
                  ref={manualAddRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.length) appendManualFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
                <p className="text-sm font-medium text-foreground">Add more photos</p>
                <p className="mt-1 text-xs text-muted">
                  Drag images here to append in order, or{" "}
                  <button
                    type="button"
                    className="font-medium text-brand-blue hover:underline"
                    onClick={() => manualAddRef.current?.click()}
                  >
                    browse
                  </button>
                </p>
              </div>
              <div className="mt-4 flex shrink-0 flex-wrap justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setManualPendingFiles(null)}
                  disabled={uploadPairsMut.isPending}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={!manualCanContinue}
                  onClick={() =>
                    uploadPairsMut.mutate({
                      files: manualPendingFiles,
                      productTail: manualOddCount ? manualProductTail : false,
                    })
                  }
                >
                  Continue
                </Button>
              </div>
            </div>
          ) : (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                onDrop(e.dataTransfer.files);
              }}
              onClick={() => fileRef.current?.click()}
              className={`cursor-pointer rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
                dragOver ? "border-brand-pink bg-brand-pink/10" : "border-line bg-surface-2/30 hover:border-line"
              }`}
            >
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) onDrop(e.target.files);
                  e.target.value = "";
                }}
              />
              <p className="text-sm font-medium text-foreground">
                {uploadMode === "manual" ? "Drop front/back photos in order" : "Drop product photos here"}
              </p>
              <p className="mt-1 text-xs text-muted">
                {uploadMode === "manual"
                  ? "Order: front, back, front, back… Optional last image for sealed product when count is odd · JPEG, PNG, WebP"
                  : "Singles, ETBs, booster packs, bundles · JPEG, PNG, or WebP · duplicates are skipped automatically"}
              </p>
              {uploadMut.isPending && <p className="mt-2 text-xs text-brand-pink">Uploading…</p>}
            </div>
          )}
        </div>

        {publishErrors.length > 0 && (
          <div className="shrink-0 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-xs text-red-200">
            <p className="font-medium">Publish errors</p>
            <ul className="mt-2 space-y-1">
              {publishErrors.map((err) => (
                <li key={err.itemId}>{err.message}</li>
              ))}
            </ul>
          </div>
        )}

        <DataTable
          columns={itemColumns}
          rows={detail?.items}
          rowKey={(r) => r.id}
          loading={detailLoading}
          exportFilename="card-uploader-items"
          fillViewport={!manualReviewOpen}
          selectable
          selectedKeys={selected}
          onSelectedKeysChange={setSelected}
          rowActions={itemRowActions}
          selection={itemSelection}
          entityLabel="Card"
          onRowClick={openEdit}
          activeKey={editItem?.id ?? null}
          empty={{ title: "No items yet", hint: "Upload photos — AI identifies singles and sealed products when configured." }}
        />
      </ListPageShell>

      <RightSidebarPanel
        open={!!editItem}
        title={editItem?.fields?.name ?? (editItem?.status === "DUPLICATE_SKIPPED" ? "Duplicate skipped" : "Review product")}
        subtitle={editItem?.status.replace(/_/g, " ")}
        onClose={() => setEditItem(null)}
        wide
        footer={
          editItem && editItem.status !== "PUBLISHED" && editItem.status !== "DUPLICATE_SKIPPED" ? (
            <div className="flex justify-end gap-2">
              {editItem.status === "FAILED" && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={retryMut.isPending}
                  onClick={() => retryMut.mutate(editItem.id)}
                >
                  Retry
                </Button>
              )}
              {(editItem.status === "IDENTIFIED" || editItem.status === "APPROVED") && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={saveItemMut.isPending}
                  onClick={() => saveItemMut.mutate({ reject: true })}
                >
                  Reject
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => setEditItem(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={saveItemMut.isPending}
                onClick={() => saveItemMut.mutate({})}
              >
                Save
              </Button>
              <Button
                size="sm"
                disabled={
                  saveItemMut.isPending ||
                  editItem.status === "APPROVED" ||
                  (editItem.status !== "IDENTIFIED" && editItem.status !== "REJECTED")
                }
                onClick={() => saveItemMut.mutate({ approve: true })}
              >
                Approve
              </Button>
            </div>
          ) : undefined
        }
      >
        {editItem && (
          <div className="sidebar-form">
            <div className="flex gap-3">
              <img src={editItem.frontUrl} alt="" className="h-40 rounded-lg object-contain ring-1 ring-white/10" />
              {editItem.backUrl && (
                <img src={editItem.backUrl} alt="" className="h-40 rounded-lg object-contain ring-1 ring-white/10" />
              )}
            </div>
            {editItem.error && (
              <p
                className={`rounded-lg px-3 py-2 text-xs ${
                  editItem.status === "DUPLICATE_SKIPPED"
                    ? "bg-white/5 text-white/60"
                    : "bg-red-500/10 text-red-300"
                }`}
              >
                {editItem.error}
              </p>
            )}
            {editItem.status === "DUPLICATE_SKIPPED" ? (
              <p className="text-xs text-white/45">
                This image matches a product already in the warehouse. Remove this row or upload a different photo.
              </p>
            ) : (
              <>
            <div className="form-row-2">
              <SearchSelect
                label="Product kind"
                options={PRODUCT_KIND_OPTIONS}
                value={fieldsDraft.productKind ?? "SINGLE"}
                onChange={(v) =>
                  setFieldsDraft((f) => ({
                    ...f,
                    productKind: v as ProductKind,
                    sealedType:
                      v === "SINGLE"
                        ? null
                        : v === "BUNDLE"
                          ? "BUNDLE"
                          : f.sealedType && f.sealedType !== "BUNDLE"
                            ? f.sealedType
                            : "OTHER",
                    graded: v === "SINGLE" ? f.graded : false,
                    gradeLabel: v === "SINGLE" ? f.gradeLabel : "",
                    number: v === "SINGLE" ? f.number : "",
                  }))
                }
              />
              {isSealedFields(fieldsDraft) && (
                <>
                  <SearchSelect
                    label="Sealed type"
                    options={SEALED_TYPE_OPTIONS}
                    value={fieldsDraft.sealedType ?? "OTHER"}
                    onChange={(v) =>
                      setFieldsDraft((f) => ({
                        ...f,
                        sealedType: v as SealedType,
                        productKind: v === "BUNDLE" ? "BUNDLE" : f.productKind === "BUNDLE" ? "SEALED" : f.productKind,
                      }))
                    }
                  />
                  {fieldsDraft.productKind === "BUNDLE" && (
                    <Input
                      label="Bundle qty (packs)"
                      type="number"
                      min={1}
                      value={fieldsDraft.bundleQty == null ? "" : String(fieldsDraft.bundleQty)}
                      onChange={(e) =>
                        setFieldsDraft((f) => ({ ...f, bundleQty: e.target.value === "" ? undefined : e.target.value }))
                      }
                    />
                  )}
                </>
              )}
            </div>
            <div className="form-row-2">
              <Input
                label="Name"
                value={fieldsDraft.name ?? ""}
                onChange={(e) => setFieldsDraft((f) => ({ ...f, name: e.target.value }))}
              />
              <Input
                label="Set"
                value={fieldsDraft.set ?? ""}
                onChange={(e) => setFieldsDraft((f) => ({ ...f, set: e.target.value }))}
              />
              {!isSealedFields(fieldsDraft) && (
                <Input
                  label="Number"
                  value={fieldsDraft.number ?? ""}
                  onChange={(e) => setFieldsDraft((f) => ({ ...f, number: e.target.value }))}
                />
              )}
              <Input
                label="Game"
                value={fieldsDraft.game ?? ""}
                onChange={(e) => setFieldsDraft((f) => ({ ...f, game: e.target.value }))}
              />
              <Input
                label="Language"
                placeholder="EN, JP, …"
                value={fieldsDraft.language ?? ""}
                onChange={(e) => setFieldsDraft((f) => ({ ...f, language: e.target.value }))}
              />
              {!isSealedFields(fieldsDraft) && (
                <Input
                  label="Variant"
                  value={fieldsDraft.variant ?? ""}
                  onChange={(e) => setFieldsDraft((f) => ({ ...f, variant: e.target.value }))}
                />
              )}
              {!isSealedFields(fieldsDraft) && (
                <SearchSelect
                  label="Condition"
                  options={CONDITION_OPTIONS}
                  value={fieldsDraft.condition ?? "Near Mint"}
                  onChange={(v) => setFieldsDraft((f) => ({ ...f, condition: v }))}
                />
              )}
              <Input
                label="Price (MYR)"
                type="number"
                step="0.01"
                value={priceDraft}
                onChange={(e) => setPriceDraft(e.target.value)}
              />
              <Input
                label="Quantity"
                type="number"
                min={1}
                value={qtyDraft}
                onChange={(e) => setQtyDraft(e.target.value)}
              />
            </div>
            {!isSealedFields(fieldsDraft) && (
            <div className="rounded-xl border border-line bg-white/[0.03] p-3 space-y-3">
              <Toggle
                label="Graded / slabbed"
                checked={Boolean(fieldsDraft.graded)}
                onChange={(v) => setFieldsDraft((f) => ({ ...f, graded: v, gradeLabel: v ? f.gradeLabel : "" }))}
              />
              {fieldsDraft.graded && (
                <Input
                  label="Grade label"
                  placeholder="PSA 10"
                  value={fieldsDraft.gradeLabel ?? ""}
                  onChange={(e) => setFieldsDraft((f) => ({ ...f, gradeLabel: e.target.value }))}
                />
              )}
            </div>
            )}
            <div className="rounded-xl border border-line bg-white/[0.03] px-3 py-2 text-xs text-white/50">
              <p className="text-white/40">Publish category (auto)</p>
              <p className="font-mono text-white/80">{previewCategoryPath(fieldsDraft)}</p>
              <p className="mt-1 text-white/40">Missing categories are created automatically on publish.</p>
            </div>
            <Textarea
              label="Notes"
              rows={2}
              value={fieldsDraft.notes ?? ""}
              onChange={(e) => setFieldsDraft((f) => ({ ...f, notes: e.target.value }))}
            />
            {fieldsDraft.confidence != null && (
              <div className="flex items-center gap-2 text-xs text-white/45">
                <Badge color={confidenceColor(fieldsDraft.confidence)}>
                  {Math.round(fieldsDraft.confidence * 100)}% confidence
                </Badge>
                {fieldsDraft.confidence < 0.6 && (
                  <span className="text-amber-300">Low confidence — double-check fields</span>
                )}
                {fieldsDraft.estPriceMYR != null && <span>· Est. {rm(fieldsDraft.estPriceMYR)}</span>}
              </div>
            )}
              </>
            )}
          </div>
        )}
      </RightSidebarPanel>

      {dialog}
    </>
  );
}
